import {
    Box,
    Alert,
    Typography,
    CircularProgress,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { fetchUser } from '../../../../services/users';
import { fetchRecord } from '../../../../services/records';
import { getPanelRole } from '../../../../services/api';
import { SelectionButton } from '../../../../component/buttons';
import PatientSectionHeading from '../components/PatientSectionHeading';
import { resolveRecordThresholds } from './recordThresholds';
import RecordInfo from './RecordInfo';
import { MapTracking } from './MapTracking';
import HrChart from './charts/HrChart';
import GroupedHrZoneCards from './charts/GroupedHrZoneCards';
import HrTimeline from './charts/HrTimeline';
import EcgViewer from './ECGViewer';
import { MainCard } from 'component/cards/MainCard2';
import { useRecordANN, useRecordHr } from 'hooks/useRecords';

const Record = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { patientUuid } = useParams();
    const { record: navigationRecord, from, seeUserState } = location.state || {};
    const recordUuid = navigationRecord?.uuid;
    const panelRole = getPanelRole();
    const recordQuery = useQuery({
        queryKey: ['record-details', panelRole, recordUuid, patientUuid],
        queryFn: async () => {
            const response = await fetchRecord(recordUuid);
            const detail = response.data;
            if (
                detail?.uuid !== recordUuid ||
                !detail?.patient?.role_uuid ||
                (patientUuid && detail.patient.role_uuid !== patientUuid)
            ) {
                throw new Error('Invalid record detail response.');
            }
            return detail;
        },
        enabled: Boolean(recordUuid),
        staleTime: 60 * 1000
    });
    const record = recordQuery.isSuccess ? recordQuery.data : null;
    const roleUuid =
        record?.patient?.role_uuid ||
        patientUuid ||
        navigationRecord?.patient?.role_uuid ||
        '';
    const [fallbackRecordUuid, setFallbackRecordUuid] = useState(null);
    const allowProfileFallback = Boolean(recordUuid && fallbackRecordUuid === recordUuid);
    const historicalResolution = resolveRecordThresholds(record, null, {
        allowProfileFallback
    });
    const needsProfile = recordQuery.isSuccess && historicalResolution.needsProfile;
    const profileQuery = useQuery({
        queryKey: ['record-threshold-profile', panelRole, roleUuid],
        queryFn: () => fetchUser(roleUuid),
        enabled: needsProfile && Boolean(roleUuid),
        staleTime: 60 * 1000
    });
    const resolution = useMemo(
        () =>
            resolveRecordThresholds(record, profileQuery.data?.data?.user_data, {
                allowProfileFallback
            }),
        [record, profileQuery.data, allowProfileFallback]
    );
    const hrThresholds = resolution.thresholds;
    const [selectedTab, setSelectedTab] = useState(0);

    const [ecgTimeWindow, setEcgTimeWindow] = useState(null);
    const deviceName =
        record?.config?.device?.name || record?.config?.name || 'Sem identificacao';
    const deviceId = record?.config?.device?.id || record?.config?.id || 'Nao disponivel';
    const firmwareVersion = record?.config?.device?.firmwareVersion || '-';

    const TYPE_LABELS = {
        rehabilitation: t('exercise.types.rehabilitation'),
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        easy: t('exercise.types.easy'),
        other: t('exercise.types.other'),
        squat: t('exercise.types.squat'),
        sit_to_stand: t('exercise.types.sit_to_stand'),
        push: t('exercise.types.push'),
        pull: t('exercise.types.pull'),
        arm_raise: t('exercise.types.arm_raise'),
        knee_extension: t('exercise.types.knee_extension')
    };

    const hasEcg = record?.config?.dataTypes?.includes('ECG');
    //const hasEcg = true;

    const tabs = [
        { label: t('records.tabs.hr'), key: 'hr' },
        ...(hasEcg ? [{ label: t('records.tabs.ecg'), key: 'ecg' }] : []),
        { label: t('records.tabs.gps'), key: 'gps' }
    ];
    const selectedTabKey = tabs[selectedTab]?.key || 'hr';

    const canLoadHr = Boolean(record && hrThresholds);

    const { hrPoints, hrMax, hrMin, dataHrZone } = useRecordHr(
        canLoadHr ? record : null,
        canLoadHr ? hrThresholds : null
    );

    const hasAnn = record?.config?.dataTypes?.includes('ANN');
    const canLoadAnn = hasAnn && Boolean(record?.uuid);

    const { data: annData } = useRecordANN(canLoadAnn ? record.uuid : null);

    const annotations = useMemo(() => {
        if (!annData?.ts?.length) return [];

        return annData.ts.map((timestamp, i) => ({
            iso: timestamp,
            time: new Date(timestamp).toLocaleTimeString('pt-PT', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }),
            label: annData.text[i],
            type: annData.type[i],
            value: annData.value[i]
        }));
    }, [annData]);

    const buildExerciseIntervals = (anns, hrPoints) => {
        if (!anns?.length || !hrPoints?.length) return [];

        const exercises = anns
            .filter((a) => a.type?.includes('E-EXERCISE'))
            .sort((a, b) => new Date(a.iso) - new Date(b.iso));

        if (!exercises.length) return [];

        const intervals = [];
        const firstIso = hrPoints[0]?.iso || record.ti;
        const firstTime = hrPoints[0]?.time;
        const lastIso = hrPoints[hrPoints.length - 1]?.iso || record.tf;
        const lastTime = hrPoints[hrPoints.length - 1]?.time;

        let currentStart = null;
        let currentLabel = null;

        for (const ann of exercises) {
            let state = null;
            try {
                state = ann.value ? JSON.parse(ann.value).state : null;
            } catch {
                state = null;
            }

            if (state === 'start') {
                currentStart = { iso: ann.iso, time: ann.time };

                const translated =
                    TYPE_LABELS[ann.label?.toLowerCase()] || ann.label || 'Exercício';
                currentLabel = translated;
            } else if (state === 'stop') {
                const startIso = currentStart?.iso || firstIso;
                const startTime = currentStart?.time || firstTime;

                const translated =
                    TYPE_LABELS[currentLabel?.toLowerCase()] ||
                    TYPE_LABELS[ann.label?.toLowerCase()] ||
                    currentLabel ||
                    ann.label ||
                    'Exercício';

                intervals.push({
                    label: translated,
                    startIso,
                    startTime,
                    endIso: ann.iso,
                    endTime: ann.time
                });

                currentStart = null;
                currentLabel = null;
            }
        }

        if (currentStart) {
            intervals.push({
                label:
                    TYPE_LABELS[currentLabel?.toLowerCase()] ||
                    currentLabel ||
                    'Exercício',
                startIso: currentStart.iso,
                startTime: currentStart.time,
                endIso: lastIso,
                endTime: lastTime
            });
        }

        return intervals;
    };

    const exerciseIntervals = useMemo(() => {
        if (!annotations.length || !hrPoints?.length) return [];
        return buildExerciseIntervals(annotations, hrPoints);
    }, [annotations, hrPoints]);

    const isLoading = recordQuery.isPending || (needsProfile && profileQuery.isPending);

    const navigateToActivity = () => {
        const activityPath =
            from?.pathname || (roleUuid ? `/users/user/${roleUuid}/atividade` : null);

        if (!activityPath) {
            navigate(-1);
            return;
        }

        navigate(activityPath, {
            state: {
                ...from?.state,
                restoredState: seeUserState
            }
        });
    };

    if (!recordUuid || recordQuery.isError || (needsProfile && profileQuery.isError)) {
        const failedQuery = recordQuery.isError ? recordQuery : profileQuery;
        return (
            <Box sx={{ m: 2 }}>
                <Alert severity="error">
                    {t(
                        !recordUuid
                            ? 'records.recordUnavailable'
                            : recordQuery.isError
                              ? 'records.recordLoadError'
                              : 'records.thresholdProfileLoadError'
                    )}
                </Alert>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    {recordUuid && (
                        <Button variant="outlined" onClick={() => failedQuery.refetch()}>
                            {t('common.retry')}
                        </Button>
                    )}
                    <Button onClick={navigateToActivity}>{t('records.back')}</Button>
                </Box>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box
                sx={{
                    height: '80vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (resolution.historicalStatus === 'invalid' && !allowProfileFallback) {
        return (
            <Box sx={{ m: 2 }}>
                <Alert severity="warning">
                    {t('records.historicalThresholdsInvalid')}
                </Alert>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        onClick={() => setFallbackRecordUuid(recordUuid)}
                    >
                        {t('records.useCurrentThresholds')}
                    </Button>
                    <Button onClick={navigateToActivity}>{t('records.back')}</Button>
                </Box>
            </Box>
        );
    }

    if (!hrThresholds) {
        return (
            <>
                <Dialog open maxWidth="sm" fullWidth>
                    <DialogTitle>{t('records.hrThresholdsMissingTitle')}</DialogTitle>

                    <DialogContent>
                        <DialogContentText>
                            {t('records.hrThresholdsMissingMessage')}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={navigateToActivity}>{t('records.back')}</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                                navigate('/users/user/edit', {
                                    state: {
                                        user: {
                                            role_uuid: roleUuid
                                        }
                                    }
                                })
                            }
                        >
                            {t('records.defineHrThresholds')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    return (
        <>
            {resolution.source === 'record' ? (
                <Typography
                    variant="caption"
                    sx={{ display: 'block', mx: 2, mt: 2, color: 'text.secondary' }}
                >
                    {t('records.historicalThresholdsSource')}
                </Typography>
            ) : (
                <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
                    {t(
                        resolution.historicalStatus === 'invalid'
                            ? 'records.invalidThresholdsProfileFallback'
                            : 'records.legacyThresholdsProfileFallback'
                    )}
                </Alert>
            )}
            <MainCard
                title={t('records.mainTitle')}
                headerAction={
                    <Box sx={{ textAlign: 'right', pt: 0.5 }}>
                        <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            <strong>Dispositivo:</strong> {deviceName}
                        </Box>
                        <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            <strong>ID:</strong> {deviceId}
                        </Box>
                        <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            <strong>{t('records.firmwareVersion')}:</strong>{' '}
                            {firmwareVersion}
                        </Box>
                    </Box>
                }
            >
                <RecordInfo
                    record={record}
                    maxHr={hrMax}
                    minHr={hrMin}
                    userData={hrThresholds}
                />
            </MainCard>

            <Box sx={{ mt: 2, px: 2 }}>
                <PatientSectionHeading
                    sectionLabel={t('userPage.sections.activity')}
                    detailLabel={t('routes.record')}
                    onBack={() => navigate(`/users/user/${roleUuid}`)}
                    onSectionBack={navigateToActivity}
                />
            </Box>

            <Grid container spacing={2} sx={{ px: 2, pb: 2, pt: 0 }}>
                {tabs.map((tab, index) => (
                    <Grid item key={tab.key} xs={4}>
                        <SelectionButton
                            selected={selectedTabKey === tab.key}
                            sx={{ width: '100%', py: 2 }}
                            onClick={() => setSelectedTab(index)}
                        >
                            {tab.label}
                        </SelectionButton>
                    </Grid>
                ))}
            </Grid>

            {selectedTabKey === 'hr' && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <HrChart
                            hrPoints={hrPoints}
                            maxHrReg={hrMax}
                            maxHr={hrThresholds.maxHeartRate}
                            basalHr={hrThresholds.restingHeartRate}
                            trainHr={hrThresholds.trainingHeartRate}
                            minTrainHr={hrThresholds.minTrainingHeartRate}
                            exerciseIntervals={exerciseIntervals}
                            onSelectEcgWindow={setEcgTimeWindow}
                            onGoToEcg={() =>
                                setSelectedTab(tabs.findIndex((t) => t.key === 'ecg'))
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <HrTimeline
                            hrPoints={hrPoints}
                            trainingHr={hrThresholds.trainingHeartRate}
                            minTrainHr={hrThresholds.minTrainingHeartRate}
                            summaryZones={dataHrZone?.summaryZones}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <MainCard title={t('records.zonesTitle')}>
                            <GroupedHrZoneCards
                                groupedZones={dataHrZone?.groupedZones || []}
                                classicZones={dataHrZone?.classicZones || []}
                            />
                        </MainCard>
                    </Grid>
                </Grid>
            )}

            {selectedTabKey === 'ecg' && (
                <MainCard title="ECG">
                    <EcgViewer
                        recordUuid={record.uuid}
                        ecgTimeWindow={ecgTimeWindow}
                        recordStart={record.ti}
                        recordEnd={record.tf}
                    />
                </MainCard>
            )}

            {selectedTabKey === 'gps' && (
                <MainCard title="GPS" contentSX={{ p: 0 }}>
                    <MapTracking uuid={record.uuid} />
                </MainCard>
            )}
        </>
    );
};

export default Record;
